/**
 * Reine Helfer der Stoffverwaltung – genutzt von Admin-API und Dashboard.
 * Vorbild: die Stoffliste des früheren ERP (`stoffe-filter.ts`, `stoffe-params.ts`).
 */
export const STOFF_UST = 0.19;
export const VK_SPANNE = { min: 20, max: 400, schritt: 5, mindestAbstand: 10 };
export const BREITEN_BEREICHE = {
    bahnbreit: [1350, 1600],
    raumhoch: [2800, 3500],
};
export const STOFF_EIGENSCHAFTEN = ["schwerEntflammbar", "recyclebar"];
export const STOFF_SORTIERUNGEN = [
    "nr",
    "hersteller",
    "name",
    "nummer",
    "breite",
    "rapport",
    "ek",
    "faktor",
    "vk",
    "gewinn",
    "aktualisiert",
    "archiviert",
];
/** Am Stoff stehen die Werte als Komma-Liste – ein Komma im Namen würde einen Wert in zwei zerlegen. */
export const WERT_OHNE_KOMMA = "Ohne Komma – je Wert ein Eintrag.";
/** Komma-Liste → Werte: getrimmt, ohne Leere, ohne Doppel (ohne Groß/klein; erste Schreibweise gewinnt). */
export function splitListe(wert) {
    const gesehen = new Set();
    const out = [];
    for (const teil of (wert ?? "").split(",")) {
        const w = teil.trim();
        if (!w || gesehen.has(w.toLowerCase()))
            continue;
        gesehen.add(w.toLowerCase());
        out.push(w);
    }
    return out;
}
/** Werte → Komma-Liste „a, b“; leer → null. */
export function listeZuText(werte) {
    const liste = splitListe(werte.join(","));
    return liste.length > 0 ? liste.join(", ") : null;
}
export function normalisiereListe(wert) {
    return listeZuText(splitListe(wert));
}
/** Vereinigt ohne Groß/klein (erste Schreibweise gewinnt), deutsch sortiert. */
export function mergeOptionen(basis, zusatz) {
    return splitListe([...basis, ...zusatz].join(",")).sort((a, b) => a.localeCompare(b, "de"));
}
export function enthaeltWert(liste, wert) {
    const ziel = wert.trim().toLowerCase();
    return splitListe(liste).some((w) => w.toLowerCase() === ziel);
}
/** Ersetzt `alt` (ohne Groß/klein) durch `neu`; Doppel fallen weg. */
export function benenneInListeUm(liste, alt, neu) {
    if (liste === null)
        return null;
    const a = alt.trim().toLowerCase();
    return listeZuText(splitListe(liste).map((w) => (w.toLowerCase() === a ? neu.trim() : w)));
}
export function vkBruttoCents(ekProLfmCents, faktor) {
    return Math.round(ekProLfmCents * faktor * (1 + STOFF_UST));
}
export function gewinnCents(ekProLfmCents, faktor) {
    return Math.round(ekProLfmCents * faktor - ekProLfmCents);
}
export function formatCm(mm) {
    return `${(mm / 10).toLocaleString("de-DE", { maximumFractionDigits: 1 })} cm`;
}
export function formatRapport(mm) {
    return mm === 0 ? "uni" : formatCm(mm);
}
export const STOFFE_PRO_SEITE = 50;
function standardSort(ansicht) {
    return ansicht === "archiv" ? { sort: "archiviert", richtung: "desc" } : { sort: "hersteller", richtung: "asc" };
}
function euro(wert) {
    if (wert === undefined || wert.trim() === "")
        return null;
    const n = Number(wert);
    if (!Number.isFinite(n))
        return null;
    return Math.min(Math.max(n, VK_SPANNE.min), VK_SPANNE.max);
}
export function leseStoffListeParams(roh) {
    const ansicht = roh.ansicht === "archiv" ? "archiv" : "aktiv";
    const standard = standardSort(ansicht);
    const sort = STOFF_SORTIERUNGEN.includes(roh.sort) ? roh.sort : standard.sort;
    const richtung = roh.richtung === "asc" || roh.richtung === "desc" ? roh.richtung : roh.sort ? "asc" : standard.richtung;
    const seite = Math.max(Math.floor(Number(roh.seite) || 1), 1);
    return {
        ansicht,
        suche: roh.suche?.trim() ?? "",
        herstellerName: roh.herstellerName?.trim() ?? "",
        breite: roh.breite === "bahnbreit" || roh.breite === "raumhoch" ? roh.breite : null,
        typ: splitListe(roh.typ),
        material: splitListe(roh.material),
        eigenschaften: splitListe(roh.eigenschaften).filter((e) => STOFF_EIGENSCHAFTEN.includes(e)),
        vkMin: euro(roh.vkMin),
        vkMax: euro(roh.vkMax),
        sort,
        richtung,
        seite,
        allePreise: roh.allePreise === "1",
    };
}
/** URL-Query aus Teil-Parametern; Standardwerte entfallen. Reihenfolge fest. */
export function stoffListeQuery(p) {
    const ansicht = p.ansicht ?? "aktiv";
    const standard = standardSort(ansicht);
    const q = new URLSearchParams();
    if (ansicht !== "aktiv")
        q.set("ansicht", ansicht);
    if (p.suche)
        q.set("suche", p.suche);
    if (p.herstellerName)
        q.set("herstellerName", p.herstellerName);
    if (p.breite)
        q.set("breite", p.breite);
    if (p.typ?.length)
        q.set("typ", p.typ.join(","));
    if (p.material?.length)
        q.set("material", p.material.join(","));
    if (p.eigenschaften?.length)
        q.set("eigenschaften", p.eigenschaften.join(","));
    if (p.vkMin != null)
        q.set("vkMin", String(p.vkMin));
    if (p.vkMax != null)
        q.set("vkMax", String(p.vkMax));
    if (p.sort && (p.sort !== standard.sort || (p.richtung ?? "asc") !== standard.richtung)) {
        q.set("sort", p.sort);
        q.set("richtung", p.richtung ?? "asc");
    }
    if (p.seite && p.seite > 1)
        q.set("seite", String(p.seite));
    if (p.allePreise)
        q.set("allePreise", "1");
    const text = q.toString();
    return text ? `?${text}` : "";
}
//# sourceMappingURL=stoffListe.js.map